import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  makeStyles,
  TextField,
  Typography,
} from '@material-ui/core';
import { DatePicker, SnackbarContext } from 'components';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { DateTimeFormat } from 'config';
import { format, parseISO } from 'date-fns';
import { updateComment } from 'lib/Api/Nps';
import { NpsComment, NpsLeader, NpsResponse } from 'lib/Model/Nps';
import * as React from 'react';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import { grey } from '@material-ui/core/colors';
import { Autocomplete } from '@material-ui/lab';
import { RichTextEditor } from './components/RichTextEditor';

export interface NpsCommentBoxHandle {
  save: () => Promise<void>;
}

const useStyles = makeStyles((theme) => ({
  commentContent: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    backgroundColor: grey[200],
    padding: theme.spacing(2),
  },
  accordion: {
    background: 'none',
    border: 0,
    padding: 0,
    '&::before': {
      background: 'none',
    },
  },
  accordionSummary: {
    padding: 0,
    margin: 0,
  },
  accordionDetails: {
    padding: 0,
  },
  actions: {
    textAlign: 'right',
  },
}));

interface Props {
  comment: NpsComment;
  selectedNpsResponse: NpsResponse;
  canEdit: boolean;
  fetchComments: () => void;
  reviewees: NpsLeader[];
  loadingReviewees: boolean;
}

export const NpsCommentBox = React.forwardRef<NpsCommentBoxHandle, Props>(
  (
    {
      comment,
      selectedNpsResponse,
      canEdit,
      fetchComments,
      reviewees,
      loadingReviewees,
    },
    ref,
  ) => {
    const classes = useStyles();
    const snackbar = React.useContext(SnackbarContext);
    const [content, setContent] = React.useState(comment.content);
    const [actionNeeded, setActionNeeded] = React.useState(
      Boolean(comment.action_needed),
    );
    const [actionBy, setActionBy] = React.useState<string | undefined>(
      comment.action_by,
    );
    const [completedBy, setCompletedBy] = React.useState<string | undefined>(
      comment.completed_by,
    );
    const [outcomeComment, setOutcomeComment] = React.useState(
      comment.outcome_comment,
    );

    React.useEffect(() => {
      setContent(comment.content);
      setActionNeeded(Boolean(comment.action_needed));
      setActionBy(comment.action_by);
      setCompletedBy(comment.completed_by);
      setOutcomeComment(comment.outcome_comment);
    }, [
      comment.id,
      comment.content,
      comment.action_needed,
      comment.action_by,
      comment.completed_by,
      comment.outcome_comment,
    ]);

    const handleSaveChanges = async () => {
      if (selectedNpsResponse.platform_nps_id) {
        try {
          await updateComment(
            selectedNpsResponse.platform_nps_id,
            comment.id,
            outcomeComment,
            actionBy,
            completedBy,
            actionNeeded ?? false,
            content,
          );
          snackbar.success('Updated.');
          fetchComments();
        } catch (e) {
          snackbar.error(`Failed to update comment: ${e}`);
        }
      }
    };

    React.useImperativeHandle(ref, () => ({ save: handleSaveChanges }));

    const actionByValue = React.useMemo(() => {
      if (!actionBy) {
        return null;
      }
      const foundReviewee = reviewees.find((r) => r.reviewee === actionBy);
      if (foundReviewee) {
        return foundReviewee;
      }

      return { reviewee: actionBy, team_identifier: '', terminated: 0 };
    }, [actionBy, reviewees]);

    return (
      <div className={classes.commentContent}>
        <Box justifyContent="space-between" alignItems="start">
          <div>
            <Typography variant="caption">
              {comment.created_on &&
                format(
                  parseISO(comment.created_on),
                  DateTimeFormat.MEDIUM_FRIENDLY,
                )}
            </Typography>
            <RichTextEditor
              disabled={!canEdit}
              value={content}
              onChange={setContent}
            />
            <Typography variant="caption">
              Author:{' '}
              {!Boolean(comment.created_by)
                ? `${selectedNpsResponse.user_first_name ?? ''} ${
                    selectedNpsResponse.user_last_name ?? ''
                  }`.trim() || 'N/A'
                : comment.created_by}
            </Typography>
          </div>
        </Box>
        <Accordion className={classes.accordion} elevation={0}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            className={classes.accordionSummary}
          >
            <FormControlLabel
              control={
                <Checkbox
                  disabled={!canEdit}
                  checked={actionNeeded}
                  onChange={(e, checked) => {
                    setActionNeeded(checked);
                  }}
                />
              }
              label="Requires Action"
            />
          </AccordionSummary>
          <AccordionDetails className={classes.accordionDetails}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <DatePicker
                  label="Completed On"
                  inputVariant="outlined"
                  InputLabelProps={{ shrink: true }}
                  size="small"
                  fullWidth
                  format="dd/MM/yyyy"
                  value={completedBy ? parseISO(completedBy) : null}
                  disabled={!canEdit}
                  onChange={(date: MaterialUiPickersDate) => {
                    setCompletedBy(date ? date.toISOString() : undefined);
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                {(reviewees.length > 0 || loadingReviewees || actionBy) && (
                  <Autocomplete
                    getOptionLabel={(option) => option.reviewee}
                    options={reviewees}
                    disabled={!canEdit || loadingReviewees}
                    loading={loadingReviewees}
                    renderInput={(params) => (
                      <TextField
                        variant="outlined"
                        label="Action By"
                        placeholder="Select an individual"
                        {...params}
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        size="small"
                      />
                    )}
                    disableClearable={false}
                    value={actionByValue}
                    getOptionSelected={(option, value) => {
                      if (option?.reviewee && value?.reviewee) {
                        return option.reviewee === value.reviewee;
                      }
                      return false;
                    }}
                    onChange={(e, newValue) => {
                      setActionBy(newValue?.reviewee || '');
                    }}
                  />
                )}
              </Grid>
              <Grid item xs={12}>
                <RichTextEditor
                  label="Outcome Comment"
                  disabled={!canEdit}
                  value={outcomeComment ?? ''}
                  placeholder="Write your comment here"
                  onChange={setOutcomeComment}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
        {canEdit && (
          <Grid item xs={12} className={classes.actions}>
            <Button variant="text" color="primary" onClick={handleSaveChanges}>
              Save Comment
            </Button>
          </Grid>
        )}
      </div>
    );
  },
);
